import React from 'react';
import { Helmet } from 'react-helmet-async';

interface FaqItem {
  question: string;
  answer: string;
}

interface JsonLdFaqProps {
  faqs: FaqItem[];
}

interface JsonLdWebsiteProps {
  url: string;
  name: string;
  description: string;
  logo?: string;
}

interface JsonLdOrganizationProps {
  name: string;
  url: string;
  logo?: string;
  sameAs?: string[];
}

interface JsonLdBreadcrumbProps {
  items: {
    name: string;
    item?: string;
  }[];
}

export const JsonLdFaq: React.FC<JsonLdFaqProps> = ({ faqs }) => {
  const faqData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(faqData)}
      </script>
    </Helmet>
  );
};

export const JsonLdWebsite: React.FC<JsonLdWebsiteProps> = ({ url, name, description, logo }) => {
  const websiteData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url,
    name,
    description,
    ...(logo && { image: logo })
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(websiteData)}
      </script>
    </Helmet>
  );
};

export const JsonLdOrganization: React.FC<JsonLdOrganizationProps> = ({ name, url, logo, sameAs }) => {
  const orgData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name,
    url,
    ...(logo && { logo }),
    ...(sameAs && { sameAs })
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(orgData)}
      </script>
    </Helmet>
  );
};

export const JsonLdBreadcrumb: React.FC<JsonLdBreadcrumbProps> = ({ items }) => {
  const breadcrumbData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      ...(item.item && { item: item.item })
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbData)}
      </script>
    </Helmet>
  );
};